import * as React from 'react';

import { cn } from '../lib/cn';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  variant?: 'accent' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  label?: React.ReactNode;
  showValue?: boolean;
}

const variantBg: Record<NonNullable<ProgressBarProps['variant']>, string> = {
  accent: 'bg-[var(--color-accent)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger)]',
};

const sizeH: Record<NonNullable<ProgressBarProps['size']>, string> = {
  sm: 'h-1',
  md: 'h-1.5',
  lg: 'h-2.5',
};

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    { value, max = 100, variant = 'accent', size = 'md', label, showValue, className, ...props },
    ref,
  ) => {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {(label || showValue) && (
          <div className="mb-1.5 flex items-center justify-between text-xs text-[var(--color-fg-secondary)]">
            <span>{label}</span>
            {showValue && (
              <span className="font-medium text-[var(--color-fg)]">
                {Math.round(pct)}%
              </span>
            )}
          </div>
        )}
        <div
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          className={cn(
            'w-full overflow-hidden rounded-full bg-[var(--color-border)]',
            sizeH[size],
          )}
        >
          <div
            className={cn('h-full rounded-full transition-all duration-300', variantBg[variant])}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  },
);
ProgressBar.displayName = 'ProgressBar';
